import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, ChannelWindow, ManifestRow, PerformanceRow, RainCheckKind, RainOutSummary } from '../../api';
import { endpoints, eventEndpoints } from '../../api';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, statusTone } from '../../ui';
import type { PanelProps } from '../Event';
import { INPUT, dollars, inZone, utcToZoned, zonedToUtc } from './shared';
import { usePermissions } from '../../session';
import { dateWords } from '../../terms';

/** Which statuses each status can move to; mirrors packages/domain/events.ts. */
const NEXT: Record<string, string[]> = {
  DRAFT: ['SCHEDULED', 'CANCELLED'],
  // POSTPONED is not offered here: postponing holds the original date, writes
  // the reason the audience reads and emails every holder, and a bare status
  // change would do none of it (D-174). It has its own button.
  SCHEDULED: ['ON_SALE', 'CANCELLED'],
  ON_SALE: ['SALES_PAUSED', 'SALES_ENDED', 'CANCELLED'],
  SALES_PAUSED: ['ON_SALE', 'SALES_ENDED', 'CANCELLED'],
  SALES_ENDED: ['ON_SALE', 'COMPLETED', 'CANCELLED'],
  POSTPONED: ['CANCELLED'],
  COMPLETED: [],
  CANCELLED: [],
  // Rained out (D-374) is only ever reached by Rain out…, which issues the rain checks.
  RAINED_OUT: [],
};

/** Where a game can be rained out from; mirrors packages/domain/events.ts. */
const RAINABLE = ['SCHEDULED', 'ON_SALE', 'SALES_PAUSED', 'SALES_ENDED', 'COMPLETED'];

const CHANNELS = ['WEB', 'BOX_OFFICE', 'PHONE', 'GROUP_SALES'];

export function PerformancesPanel({ detail, refresh, onError, can }: PanelProps) {
  const [adding, setAdding] = useState(false);
  const [refunding, setRefunding] = useState<string | null>(null);
  const [rainingOut, setRainingOut] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [windowsFor, setWindowsFor] = useState<string | null>(null);
  /** What the last postponement or reschedule did, since both act on people's tickets. */
  const [notice, setNotice] = useState<string | null>(null);
  const tz = detail.venue.timezone;
  const editable = can('performances.manage') && detail.event.status !== 'ARCHIVED' && detail.event.status !== 'CANCELLED';

  const manifests = useQuery({ queryKey: ['venue', detail.venue.id], queryFn: () => endpoints.venue(detail.venue.id) });
  const published: ManifestRow[] = (manifests.data?.manifests ?? []).filter((m) => m.status === 'PUBLISHED');

  /**
   * Postponing and rescheduling (D-174).
   *
   * Both are their own endpoints rather than a status change, because both do
   * things a status cannot: hold the date the audience bought, email every
   * holder, and move the tickets.
   */
  const postpone = useMutation({
    mutationFn: ({ performanceId, reason }: { performanceId: string; reason: string }) => eventEndpoints.postponePerformance(detail.event.id, performanceId, reason),
    onSuccess: (result) => {
      setNotice(`Postponed. ${result.notified} ticket holder${result.notified === 1 ? '' : 's'} emailed; their tickets stay valid and the message offers a refund.`);
      refresh();
    },
    onError,
  });

  const reschedule = useMutation({
    mutationFn: ({ performanceId, starts_at, on_sale }: { performanceId: string; starts_at: string; on_sale: boolean }) => eventEndpoints.reschedulePerformance(detail.event.id, performanceId, { starts_at, on_sale }),
    onSuccess: (result) => {
      setNotice(`New date set. ${result.reissued} ticket${result.reissued === 1 ? '' : 's'} reissued and ${result.notified} holder${result.notified === 1 ? '' : 's'} emailed.`);
      refresh();
    },
    onError,
  });

  /** A single-admission doubleheader (D-374): one ticket, both games. */
  const doubleheader = useMutation({
    mutationFn: ({ performanceId, starts_at, notify }: { performanceId: string; starts_at: string; notify: boolean }) => eventEndpoints.makeDoubleheader(detail.event.id, performanceId, { starts_at, notify_holders: notify }),
    onSuccess: (result) => {
      setNotice(`Now “${result.title}”, game 1 at ${inZone(result.performance.starts_at, tz)}, noted “${result.note}”. Tickets stay valid; ${result.notified} holder${result.notified === 1 ? '' : 's'} emailed.`);
      refresh();
    },
    onError,
  });

  /** An "if necessary" game the series did not need (D-374): cancel, email, refund the first page. */
  const notNeeded = useMutation({
    mutationFn: (performanceId: string) => eventEndpoints.notNeeded(detail.event.id, performanceId),
    onSuccess: (result, performanceId) => {
      setNotice(`Canceled as not needed; every holder was emailed. ${result.refunded_orders.length} order${result.refunded_orders.length === 1 ? '' : 's'} refunded so far${result.summary.orders_outstanding > 0 ? `, ${result.summary.orders_outstanding} to go under Refund tickets…` : ''}.`);
      setRefunding(performanceId);
      refresh();
    },
    onError,
  });

  const setStatus = useMutation({
    mutationFn: ({ performanceId, status, reason }: { performanceId: string; status: string; reason?: string }) =>
      eventEndpoints.setPerformanceStatus(detail.event.id, performanceId, status, reason),
    onSuccess: refresh,
    onError,
  });

  return (
    <div className="space-y-4">
      {notice ? <p className="rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">{notice}</p> : null}
      {adding && editable ? <PerformanceForm detail={detail} manifests={published} onDone={() => { setAdding(false); refresh(); }} onCancel={() => setAdding(false)} onError={onError} /> : null}
      <Panel
        title={`${dateWords().Many}`}
        description={`Times are shown in ${tz}. ${dateWords().A} sells into one published layout, which cannot change once its inventory exists.`}
        actions={
          editable && !adding ? (
            <Button variant="primary" onClick={() => setAdding(true)} disabled={published.length === 0} title={published.length === 0 ? 'Publish a manifest for this venue first' : undefined}>
              Add {dateWords().a}
            </Button>
          ) : null
        }
      >
        <Table headers={['When', 'Code', 'Layout', 'Status', 'Sales windows', '']}>
          {detail.performances.length === 0 ? (
            <EmptyRow columns={6}>{published.length === 0 ? 'This venue has no published layout yet; publish one under Venues first.' : `No ${dateWords().many} yet.`}</EmptyRow>
          ) : (
            detail.performances.map((p) =>
              editing === p.id ? (
                <tr key={p.id} className="border-b border-line last:border-0">
                  <td colSpan={6} className="px-5 py-3">
                    <PerformanceForm detail={detail} manifests={published} existing={p} onDone={() => { setEditing(null); refresh(); }} onCancel={() => setEditing(null)} onError={onError} inline />
                  </td>
                </tr>
              ) : (
              <Fragment key={p.id}>
              <tr className="border-b border-line align-top last:border-0">
                <td className="px-5 py-3 text-sm text-ink-900">
                  {inZone(p.starts_at, tz)}
                  {p.doors_at ? <div className="text-xs text-ink-400">{dateWords().gates.toLowerCase()} {inZone(p.doors_at, tz).split(', ').pop()}</div> : null}
                  {p.name_override ? <div className="text-xs text-ink-600">{p.name_override}</div> : null}
                </td>
                <td className="px-5 py-3 text-xs text-ink-400">{p.code}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{published.find((m) => m.id === p.manifest_id)?.name ?? manifests.data?.manifests.find((m) => m.id === p.manifest_id)?.name ?? '…'}</td>
                <td className="px-5 py-3">
                  <Badge tone={p.status === 'ON_SALE' ? 'ok' : p.status === 'CANCELLED' || p.status === 'RAINED_OUT' ? 'bad' : p.status === 'SALES_PAUSED' || p.status === 'POSTPONED' ? 'warn' : statusTone(p.status)}>{p.status.replaceAll('_', ' ')}</Badge>
                  {p.status === 'RAINED_OUT' && p.rain_out_reason ? <div className="mt-1 text-xs text-ink-600">{p.rain_out_reason}</div> : null}
                  {!p.inventory_initialized_at && p.status !== 'CANCELLED' && p.status !== 'COMPLETED' ? (
                    <div className="mt-1 text-xs text-warn">No inventory yet — initialize it on the Inventory tab before selling.</div>
                  ) : null}
                </td>
                <td className="px-5 py-3 text-xs text-ink-600">
                  {windowsFor === p.id ? (
                    <WindowsEditor detail={detail} performance={p} onDone={() => { setWindowsFor(null); refresh(); }} onCancel={() => setWindowsFor(null)} onError={onError} />
                  ) : (
                    <>
                      <WindowsSummary windows={p.channel_windows ?? []} tz={tz} />
                      {editable ? (
                        <button type="button" className="mt-1 text-accent underline-offset-2 hover:underline" onClick={() => setWindowsFor(p.id)}>
                          Edit windows
                        </button>
                      ) : null}
                    </>
                  )}
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {p.status === 'CANCELLED' && can('orders.view') ? (
                    <Button className="ml-1" onClick={() => setRefunding((current) => (current === p.id ? null : p.id))}>
                      {refunding === p.id ? 'Hide refunds' : 'Refund tickets…'}
                    </Button>
                  ) : null}
                  {can('events.view') && (RAINABLE.includes(p.status) || p.status === 'RAINED_OUT') && (p.status === 'RAINED_OUT' || can('performances.rain_out')) ? (
                    <Button className="ml-1" variant={p.status === 'RAINED_OUT' ? 'secondary' : 'danger'} onClick={() => setRainingOut((current) => (current === p.id ? null : p.id))}>
                      {rainingOut === p.id ? 'Hide rain out' : p.status === 'RAINED_OUT' ? 'Rain checks…' : 'Rain out…'}
                    </Button>
                  ) : null}
                  {editable && can('events.publish') && ['SCHEDULED', 'ON_SALE', 'SALES_PAUSED', 'SALES_ENDED'].includes(p.status) && detail.event.doubleheader !== 1 ? (
                    <Button
                      className="ml-1"
                      disabled={doubleheader.isPending}
                      onClick={() => {
                        const when = window.prompt(
                          `Make ${detail.event.title} a single-admission doubleheader: one ticket, both games.\n\nGame 1 starts at (in ${p.timezone.replaceAll('_', ' ')}):\n\nThe game is retitled and noted “Two games, one ticket” (Admin → Settings → Rain checks), the opening time moves with the start, and tickets stay valid.`,
                          utcToZoned(p.starts_at, p.timezone),
                        );
                        if (!when) return;
                        const instant = zonedToUtc(when, p.timezone);
                        if (!instant) {
                          onError({ code: 'BAD_DATE', message: `"${when}" is not a date and time — use 2027-06-13 17:05`, status: 400 } as ApiError);
                          return;
                        }
                        doubleheader.mutate({ performanceId: p.id, starts_at: instant, notify: window.confirm('Email everybody holding a ticket that it is now a doubleheader?') });
                      }}
                    >
                      Make doubleheader…
                    </Button>
                  ) : null}
                  {detail.event.if_necessary === 1 && can('events.publish') && can('refunds.approve') && !['CANCELLED', 'COMPLETED', 'RAINED_OUT', 'DRAFT'].includes(p.status) ? (
                    <Button
                      className="ml-1"
                      variant="danger"
                      disabled={notNeeded.isPending}
                      onClick={() => {
                        if (window.confirm('Not needed: cancel this “if necessary” game and refund every ticket, face value and fees?\n\nEvery holder is emailed that the game is not needed and that their refund is automatic.')) notNeeded.mutate(p.id);
                      }}
                    >
                      Not needed: cancel and refund
                    </Button>
                  ) : null}
                  {editable && p.status !== 'CANCELLED' && p.status !== 'COMPLETED' && p.status !== 'RAINED_OUT' ? (
                    <Button className="ml-1" onClick={() => setEditing(p.id)}>
                      Edit
                    </Button>
                  ) : null}
                  {editable && can('events.publish') && ['SCHEDULED', 'ON_SALE', 'SALES_PAUSED', 'SALES_ENDED'].includes(p.status) ? (
                    <Button
                      className="ml-1"
                      disabled={postpone.isPending}
                      onClick={() => {
                        const reason = window.prompt(`Why is this ${dateWords().one} being postponed?\n\nCustomers read this, so write it for them. Everybody holding a ticket is emailed, their tickets stay valid, and the message tells them a refund is available.`);
                        if (reason?.trim()) postpone.mutate({ performanceId: p.id, reason: reason.trim() });
                      }}
                    >
                      Postpone…
                    </Button>
                  ) : null}
                  {editable && can('events.publish') && p.status === 'POSTPONED' ? (
                    <Button
                      className="ml-1"
                      variant="primary"
                      disabled={reschedule.isPending}
                      onClick={() => {
                        const when = window.prompt(`New date and time for ${detail.event.title}, in ${p.timezone.replaceAll('_', ' ')}:\n\nEvery ticket is reissued at the new date, so copies saved before now will stop scanning. Everybody holding one is emailed the new ones.`, utcToZoned(p.starts_at, p.timezone));
                        const instant = when ? zonedToUtc(when, p.timezone) : null;
                        if (!when) return;
                        if (!instant) {
                          onError({ code: 'BAD_DATE', message: `"${when}" is not a date and time — use 2027-03-13 19:00`, status: 400 } as ApiError);
                          return;
                        }
                        reschedule.mutate({ performanceId: p.id, starts_at: instant, on_sale: window.confirm('Put it back on sale straight away?') });
                      }}
                    >
                      Set new date…
                    </Button>
                  ) : null}
                  {editable
                    ? (NEXT[p.status] ?? []).map((next) => (
                        <Button
                          key={next}
                          className="ml-1"
                          variant={next === 'CANCELLED' ? 'danger' : next === 'ON_SALE' ? 'primary' : 'secondary'}
                          disabled={setStatus.isPending || (next === 'CANCELLED' && !can('events.publish'))}
                          onClick={() => {
                            if (next === 'CANCELLED') {
                              const reason = window.prompt(`Why is this ${dateWords().one} being canceled?`);
                              if (reason) setStatus.mutate({ performanceId: p.id, status: next, reason });
                            } else setStatus.mutate({ performanceId: p.id, status: next });
                          }}
                        >
                          {next === 'ON_SALE' ? 'Put on sale' : next.replaceAll('_', ' ').toLowerCase()}
                        </Button>
                      ))
                    : null}
                </td>
              </tr>
              {rainingOut === p.id ? (
                <tr className="border-b border-line">
                  <td colSpan={6} className="bg-page px-5 py-4">
                    <RainOut eventId={detail.event.id} performance={p} onError={onError} onDone={refresh} />
                  </td>
                </tr>
              ) : null}
              {refunding === p.id ? (
                <tr className="border-b border-line">
                  <td colSpan={6} className="bg-page px-5 py-4">
                    <CancellationRefunds eventId={detail.event.id} performanceId={p.id} onError={onError} />
                  </td>
                </tr>
              ) : null}
              </Fragment>
              ),
            )
          )}
        </Table>
      </Panel>
    </div>
  );
}

function WindowsSummary({ windows, tz }: { windows: ChannelWindow[]; tz: string }) {
  if (windows.length === 0) return <span className="text-ink-400">every channel, no window</span>;
  return (
    <ul>
      {windows.map((w) => (
        <li key={w.channel}>
          {w.channel.replaceAll('_', ' ').toLowerCase()}: {w.enabled ? `${w.opens_at ? inZone(w.opens_at, tz) : 'open'} → ${w.closes_at ? inZone(w.closes_at, tz) : 'showtime'}` : 'off'}
        </li>
      ))}
    </ul>
  );
}

function WindowsEditor({ detail, performance, onDone, onCancel, onError }: { detail: PanelProps['detail']; performance: PerformanceRow; onDone: () => void; onCancel: () => void; onError: PanelProps['onError'] }) {
  const tz = detail.venue.timezone;
  const existing = new Map((performance.channel_windows ?? []).map((w) => [w.channel, w]));
  const [rows, setRows] = useState(
    CHANNELS.map((channel) => {
      const w = existing.get(channel);
      return { channel, enabled: w ? Boolean(w.enabled) : true, opens: utcToZoned(w?.opens_at, tz), closes: utcToZoned(w?.closes_at, tz) };
    }),
  );
  const save = useMutation({
    mutationFn: () =>
      eventEndpoints.setChannelWindows(
          detail.event.id,
          performance.id,
          rows.map((r) => ({ channel: r.channel, enabled: r.enabled, opens_at: zonedToUtc(r.opens, tz), closes_at: zonedToUtc(r.closes, tz) })),
        ),
    onSuccess: onDone,
    onError,
  });
  return (
    <div className="space-y-1">
      {rows.map((r, i) => (
        <div key={r.channel} className="flex flex-wrap items-center gap-2">
          <label className="flex w-28 items-center gap-1">
            <input type="checkbox" checked={r.enabled} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, enabled: e.target.checked } : x)))} />
            {r.channel.replaceAll('_', ' ').toLowerCase()}
          </label>
          <input type="datetime-local" value={r.opens} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, opens: e.target.value } : x)))} className={INPUT} aria-label={`${r.channel} opens`} />
          <span>→</span>
          <input type="datetime-local" value={r.closes} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, closes: e.target.value } : x)))} className={INPUT} aria-label={`${r.channel} closes`} />
        </div>
      ))}
      <div className="flex gap-2 pt-1">
        <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
          Save windows
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function PerformanceForm({ detail, manifests, existing, onDone, onCancel, onError, inline }: { detail: PanelProps['detail']; manifests: ManifestRow[]; existing?: PerformanceRow; onDone: () => void; onCancel: () => void; onError: PanelProps['onError']; inline?: boolean }) {
  const tz = detail.venue.timezone;
  const [manifestId, setManifestId] = useState(existing?.manifest_id ?? manifests[0]?.id ?? '');
  const [starts, setStarts] = useState(utcToZoned(existing?.starts_at, tz));
  const [doors, setDoors] = useState(utcToZoned(existing?.doors_at, tz));
  const [ends, setEnds] = useState(utcToZoned(existing?.ends_at, tz));
  const [name, setName] = useState(existing?.name_override ?? '');
  const [reentry, setReentry] = useState(existing?.reentry_policy ?? 'SINGLE_ENTRY');
  const [notes, setNotes] = useState(existing?.public_notes ?? '');
  const manifestLocked = Boolean(existing?.inventory_initialized_at);
  const create = useMutation({
    mutationFn: () => {
      const input = {
        ...(manifestLocked ? {} : { manifest_id: manifestId }),
        starts_at: zonedToUtc(starts, tz),
        doors_at: zonedToUtc(doors, tz),
        ends_at: zonedToUtc(ends, tz),
        name_override: name.trim() || null,
        reentry_policy: reentry,
        public_notes: notes.trim() || null,
      };
      if (!existing) return eventEndpoints.createPerformance(detail.event.id, input);
      /*
       * A moved start time is news for everybody holding a ticket (D-374). The
       * API emails them by default when anything is sold; staff may say no,
       * say for a typo fixed before anybody could have noticed.
       */
      const moved = input.starts_at !== existing.starts_at;
      const notify = moved ? window.confirm('The start time changed. Email everybody holding a ticket about the new time?\n\nOK emails them (tickets stay valid; nothing is reissued). Cancel changes it without telling anybody.') : undefined;
      return eventEndpoints.updatePerformance(detail.event.id, existing.id, { ...input, ...(notify === undefined ? {} : { notify_holders: notify }) });
    },
    onSuccess: onDone,
    onError,
  });
  const form = (
      <form
        className={`flex flex-wrap items-end gap-4 ${inline ? '' : 'px-5 py-4'}`}
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Starts" htmlFor="perf-starts">
          <input id="perf-starts" type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)} className={INPUT} />
        </Field>
        <Field label={dateWords().gates} htmlFor="perf-doors">
          <input id="perf-doors" type="datetime-local" value={doors} onChange={(e) => setDoors(e.target.value)} className={INPUT} />
        </Field>
        <Field label="Ends" htmlFor="perf-ends">
          <input id="perf-ends" type="datetime-local" value={ends} onChange={(e) => setEnds(e.target.value)} className={INPUT} />
        </Field>
        <Field label="Layout" htmlFor="perf-manifest" hint={manifestLocked ? 'Fixed once inventory exists' : undefined}>
          <select id="perf-manifest" value={manifestId} onChange={(e) => setManifestId(e.target.value)} disabled={manifestLocked} className={INPUT}>
            {manifests.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} v{m.version}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Re-entry" htmlFor="perf-reentry">
          <select id="perf-reentry" value={reentry} onChange={(e) => setReentry(e.target.value)} className={INPUT}>
            <option value="SINGLE_ENTRY">single entry</option>
            <option value="REENTRY_ALLOWED">re-entry allowed</option>
          </select>
        </Field>
        <Field label="Name (optional)" htmlFor="perf-name">
          <input id="perf-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Afternoon" className={`w-48 ${INPUT}`} />
        </Field>
        <Field label="Public notes" htmlFor="perf-notes">
          <input id="perf-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Entry may open late" className={`w-64 ${INPUT}`} />
        </Field>
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={!starts || !manifestId || create.isPending}>
            {create.isPending ? 'Saving…' : existing ? 'Save' : `Add ${dateWords().one}`}
          </Button>
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
  );
  return inline ? form : <Panel title={`Add ${dateWords().a}`} description={`Enter times as they will read on the ticket, in ${tz}.`}>{form}</Panel>;
}

/**
 * Refunding a canceled performance (D-72). Every ticket still live on it goes back to the
 * way it was paid, fees included; a ticket inside a package refunds its prorated share of
 * the package price. The batch is paged, so pressing it again simply carries on.
 */
function CancellationRefunds({ eventId, performanceId, onError }: { eventId: string; performanceId: string; onError: PanelProps['onError'] }) {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [refundFees, setRefundFees] = useState(true);
  const [log, setLog] = useState<string[]>([]);
  const summary = useQuery({ queryKey: ['cancellation', performanceId], queryFn: () => eventEndpoints.cancellation(eventId, performanceId) });

  const run = useMutation({
    mutationFn: () => eventEndpoints.refundCancelled(eventId, performanceId, { limit: 20, refund_fees: refundFees }),
    onSuccess: (result) => {
      setLog((current) => [
        ...current,
        ...result.refunded_orders.map((r) => `${r.order_number}: ${dollars(r.amount_cents)} back over ${r.tickets} ticket${r.tickets === 1 ? '' : 's'} (${r.refund_number})${r.stripe_ok ? '' : ` — STRIPE REFUND FAILED: ${r.message ?? ''}`}`),
        ...result.failed_orders.map((r) => `${r.order_number}: NOT refunded — ${r.message}`),
      ]);
      void queryClient.invalidateQueries({ queryKey: ['cancellation', performanceId] });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError,
  });

  const data = summary.data;
  if (!data) return <p className="text-sm text-ink-400">Loading…</p>;
  const done = data.orders_outstanding === 0;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-6 text-sm">
        <span>
          <span className="text-ink-400">Still to refund:</span> <span className="tabular font-semibold">{data.orders_outstanding}</span> order{data.orders_outstanding === 1 ? '' : 's'} · {data.tickets_outstanding} ticket{data.tickets_outstanding === 1 ? '' : 's'} · {dollars(data.outstanding_value_cents)}
        </span>
        <span>
          <span className="text-ink-400">Refunded so far:</span> <span className="tabular">{dollars(data.refunded_cents)}</span> over {data.tickets_refunded} ticket{data.tickets_refunded === 1 ? '' : 's'}
        </span>
        {data.package_instances > 0 ? (
          <span>
            <span className="text-ink-400">Packages affected:</span> {data.package_instances} (each refunded its prorated share)
          </span>
        ) : null}
      </div>
      {data.failed_refunds.length > 0 ? (
        <ErrorNote>
          {data.failed_refunds.map((r) => `${r.order_number} (${r.refund_number}): ${r.status.toLowerCase()}${r.message ? ` — ${r.message}` : ''}`).join('; ')}. Fix these on the order, or pay the customer another way.
        </ErrorNote>
      ) : null}
      <label className="flex items-center gap-2 text-sm text-ink-600">
        <input type="checkbox" checked={refundFees} onChange={(e) => setRefundFees(e.target.checked)} /> Refund non-refundable fees too (we canceled, so normally yes)
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" disabled={run.isPending || done || !can('refunds.approve')} onClick={() => run.mutate()}>
          {run.isPending ? 'Refunding…' : done ? 'Nothing left to refund' : `Refund the next ${Math.min(20, data.orders_outstanding)} order${Math.min(20, data.orders_outstanding) === 1 ? '' : 's'}`}
        </Button>
        {!can('refunds.approve') ? <span className="text-xs text-ink-400">Refunding needs refunds.approve.</span> : null}
        {!done && !run.isPending ? <span className="text-xs text-ink-400">Press again until nothing is left; each press picks up where the last one stopped.</span> : null}
      </div>
      {log.length > 0 ? (
        <ul className="max-h-56 space-y-0.5 overflow-y-auto rounded border border-line bg-surface px-3 py-2 text-xs text-ink-600">
          {log.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

const KIND_WORDS: Record<RainCheckKind, string> = {
  CREDIT: 'account credit',
  COMP: 'comps (nothing to give back)',
  SEASON_PASS: 'season-pass tickets (the pass covers the makeup)',
  VOUCHER: 'voucher tickets (the voucher is replaced)',
};

/**
 * Raining a game out (D-374). The dry run comes first and is the whole
 * confirmation: how many tickets, how many already scanned, what each kind
 * becomes and what the credit comes to. Then one press marks the game rained
 * out and works through every order, page by page, scanned tickets included.
 * The makeup is a separate choice, set out beside it.
 */
function RainOut({ eventId, performance, onError, onDone }: { eventId: string; performance: PerformanceRow; onError: PanelProps['onError']; onDone: () => void }) {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState(performance.rain_out_reason ?? '');
  const [log, setLog] = useState<string[]>([]);
  const summary = useQuery({ queryKey: ['rain-out', performance.id], queryFn: () => eventEndpoints.rainOutSummary(eventId, performance.id) });

  const run = useMutation({
    mutationFn: async () => {
      let cursor: string | null = null;
      let last: RainOutSummary | null = null;
      do {
        const page = await eventEndpoints.rainOut(eventId, performance.id, { reason: reason.trim(), limit: 20, after_order_id: cursor });
        setLog((current) => [
          ...current,
          ...page.processed_orders.map((o) => `${o.order_number}: ${o.credit_cents > 0 ? `${dollars(o.credit_cents)} credit` : 'no credit'} over ${o.tickets} ticket${o.tickets === 1 ? '' : 's'} (${o.kinds.map((k) => k.toLowerCase().replace('_', ' ')).join(', ')})`),
          ...page.failed_orders.map((o) => `${o.order_number}: NOT done — ${o.message}`),
        ]);
        cursor = page.next_after_order_id;
        last = page.summary;
      } while (cursor);
      return last;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rain-out', performance.id] });
      onDone();
    },
    onError,
  });

  const data = summary.data;
  if (!data) return <p className="text-sm text-ink-400">Loading…</p>;
  const rained = data.status === 'RAINED_OUT';
  const out = data.outstanding;
  const credit = out.by_kind.CREDIT;
  const until = data.valid_through ? `until ${data.valid_through}` : 'with no end date';
  const fees = data.policy.include_fees ? 'face value and fees' : 'face value only (fees stay)';
  return (
    <div className="space-y-3 text-sm">
      {rained ? (
        <p>
          <span className="font-semibold text-ink-900">Rained out</span>
          {data.rained_out_at ? ` ${data.rained_out_at.slice(0, 16).replace('T', ' ')} UTC` : ''}. Issued: {data.issued.tickets} rain check{data.issued.tickets === 1 ? '' : 's'}, {dollars(data.issued.by_kind.CREDIT.cents)} of credit to {data.issued.customers} customer{data.issued.customers === 1 ? '' : 's'}
          {data.issued.replacement_vouchers > 0 ? `, ${data.issued.replacement_vouchers} voucher${data.issued.replacement_vouchers === 1 ? '' : 's'} replaced` : ''}.
        </p>
      ) : (
        <p className="text-ink-900">
          Rain out <strong>{data.event_title}</strong>: sales stop at once, and every ticket on it — <strong>scanned in or not</strong> — becomes a rain check. Nothing happens until you press the button below.
        </p>
      )}
      {out.tickets > 0 ? (
        <div className="rounded border border-line bg-surface px-3 py-2">
          <p className="font-semibold text-ink-900">
            {rained ? 'Still to do' : 'What it will do'}: {out.tickets} ticket{out.tickets === 1 ? '' : 's'} on {out.orders} order{out.orders === 1 ? '' : 's'}
            {out.admitted > 0 ? `, ${out.admitted} already scanned in` : ''}
          </p>
          <ul className="mt-1 list-disc pl-5 text-ink-600">
            {credit.tickets > 0 ? (
              <li>
                {credit.tickets} → about <span className="tabular font-semibold">{dollars(credit.cents)}</span> of account credit ({fees}), to each buyer’s account (a guest’s waits on the account for their email), spendable {until}
              </li>
            ) : null}
            {(['COMP', 'VOUCHER', 'SEASON_PASS'] as const).map((kind) => (out.by_kind[kind].tickets > 0 ? <li key={kind}>{out.by_kind[kind].tickets} {KIND_WORDS[kind]}</li> : null))}
          </ul>
          {out.no_account_orders > 0 ? (
            <p className="mt-1 text-warn">{out.no_account_orders} paid order{out.no_account_orders === 1 ? ' has' : 's have'} no customer or email, so the credit has nowhere to go: add the buyer’s email to the order and run it again, or settle it at the window.</p>
          ) : null}
          <p className="mt-1 text-xs text-ink-400">Buyers are emailed what their rain check is worth and how to use it. Change what it is worth and how long it lasts under Settings → Rain checks.</p>
        </div>
      ) : rained ? (
        <p className="text-ink-600">Every ticket has its rain check.</p>
      ) : (
        <p className="text-ink-600">No tickets are sold for this game; raining it out only stops sales.</p>
      )}
      {!rained ? (
        <div className="rounded border border-line bg-surface px-3 py-2 text-ink-600">
          <p className="font-semibold text-ink-900">The makeup game</p>
          <ul className="mt-1 list-disc pl-5">
            <li className={data.policy.doubleheader_default === 'SINGLE_ADMISSION' ? 'font-semibold text-ink-900' : ''}>
              <strong>Single-admission doubleheader</strong>{data.policy.doubleheader_default === 'SINGLE_ADMISSION' ? ' (the default)' : ''}: rain this game out here, then on the doubleheader day’s game press <em>Make doubleheader…</em>. Its tickets admit to both games; these holders use their rain checks.
            </li>
            <li className={data.policy.doubleheader_default === 'SEPARATE_ADMISSION' ? 'font-semibold text-ink-900' : ''}>
              <strong>Separate admission</strong>{data.policy.doubleheader_default === 'SEPARATE_ADMISSION' ? ' (the default)' : ''}: do <em>not</em> rain it out. Press <em>Postpone…</em> on this game, then <em>Set new date…</em> to game 1’s time on the doubleheader day: its tickets are reissued for that game and every holder is emailed.
            </li>
          </ul>
        </div>
      ) : null}
      {data.can_rain_out && (out.tickets > 0 || !rained) && can('performances.rain_out') ? (
        <div className="flex flex-wrap items-end gap-2">
          {!rained ? (
            <label className="block">
              <span className="block text-xs text-ink-400">Why it was called (buyers read this)</span>
              <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="Rain; the game was called in the 3rd" className={`w-96 ${INPUT}`} />
            </label>
          ) : null}
          <Button
            variant="danger"
            disabled={run.isPending || (!rained && !reason.trim())}
            onClick={() => {
              if (rained || window.confirm(`Rain out ${data.event_title}?\n\nSales stop and ${out.tickets} ticket${out.tickets === 1 ? '' : 's'} become rain checks (${dollars(credit.cents)} of credit). This cannot be undone.`)) run.mutate();
            }}
          >
            {run.isPending ? 'Issuing rain checks…' : rained ? 'Issue the rest' : 'Rain out and issue rain checks'}
          </Button>
        </div>
      ) : null}
      {log.length > 0 ? (
        <ul className="max-h-56 space-y-0.5 overflow-y-auto rounded border border-line bg-surface px-3 py-2 text-xs text-ink-600">
          {log.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
